import {
  PublicRatingSerializer,
  FormattedRating,
  RecommendedRating,
} from "../types/ratings";

/**
 * Rating display thresholds
 * These are the minimum requirements for a rating to be displayed publicly
 */
export const RATING_TOTAL_THRESHOLD = 5;
export const RATING_AVERAGE_THRESHOLD = 4.0;
const PERCENTAGE_RATING_THRESHOLD = 90;

/**
 * Checks if a rating has the minimum number of reviews required for display.
 * Requires at least 5 ratings.
 */
export const hasMinimumTotalCustomerReviews = (
  rating?: PublicRatingSerializer
): boolean => {
  return Boolean(rating && rating.total_ratings >= RATING_TOTAL_THRESHOLD);
};

/**
 * Checks if a rating meets the minimum average threshold for display.
 * Requires at least 4.0 average.
 */
export const hasMinimumAverageCustomerReviews = (
  rating?: PublicRatingSerializer
): boolean => {
  return Boolean(
    rating &&
      parseFloat(String(rating.average_rating)) >= RATING_AVERAGE_THRESHOLD
  );
};

/**
 * Check if a rating meets all thresholds to be displayed publicly.
 * Requires both minimum total ratings AND minimum average rating.
 */
export const shouldDisplayRating = (
  rating?: PublicRatingSerializer
): boolean => {
  return (
    hasMinimumTotalCustomerReviews(rating) &&
    hasMinimumAverageCustomerReviews(rating)
  );
};

/**
 * Format a rating for display.
 * Returns null if the rating doesn't meet display thresholds.
 */
export const getFormattedRating = (
  rating?: PublicRatingSerializer
): FormattedRating | null => {
  if (!rating || !shouldDisplayRating(rating)) return null;

  const avgRating = parseFloat(String(rating.average_rating));

  return {
    average: avgRating.toFixed(1),
    total: rating.total_ratings,
  };
};

/**
 * Get the rating to display, preferring event rating over venue rating.
 * This implements the rating display hierarchy:
 * 1. Preferred rating e.g. Event-level rating (if available and meets thresholds)
 * 2. Fallback rating e.g. Venue rating (if available and meets thresholds)
 * 3. null (if neither meets thresholds)
 */
export const getDisplayableRating = (
  preferredRating?: PublicRatingSerializer,
  fallbackRating?: PublicRatingSerializer
): FormattedRating | null => {
  // Prefer event rating if it meets display criteria
  const formattedPreferredRating = getFormattedRating(preferredRating);
  if (formattedPreferredRating) return formattedPreferredRating;

  // Fall back to venue rating
  return getFormattedRating(fallbackRating);
};

/**
 * Calculate the percentage of ratings that are 4+ stars
 * Formula: (total 4-star rating count + total 5-star rating count) / all total ratings × 100 ≥ 90
 *
 * The 90% threshold ensures we only show this for highly-rated items.
 * Note: A 4.0 average rating could have as few as 50% of ratings being 4+ stars
 * (e.g., half 3-star, half 5-star = 4.0 average but only 50% are 4+)
 */
export const getRecommendedRating = (
  rating?: PublicRatingSerializer
): RecommendedRating => {
  let isRecommended = false;
  let recommendedPercentage = 0;

  if (
    rating &&
    hasMinimumTotalCustomerReviews(rating) &&
    hasMinimumAverageCustomerReviews(rating)
  ) {
    const fourStarCount = rating.distribution["4"] || 0;
    const fiveStarCount = rating.distribution["5"] || 0;

    recommendedPercentage = Math.round(
      ((fourStarCount + fiveStarCount) / rating.total_ratings) * 100
    );

    isRecommended = recommendedPercentage >= PERCENTAGE_RATING_THRESHOLD;
  }

  return {
    isRecommended,
    recommendedPercentage,
  };
};
